;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();import { analytics } from '@/lib/analytics/posthog'

import {
  bing,
  conversionValue,
  eventNames,
  google,
  linkedin,
  meta,
  pixelsEnabled,
  tiktok,
  type ConversionType,
} from '@/config/analytics'
import { normalizeEmail, normalizePhone } from '@/lib/analytics/identity'

type Gtag = (command: string, action: string, params?: Record<string, unknown>) => void
type Uet = { push: (...args: unknown[]) => void }
type Ttq = {
  identify: (data: Record<string, unknown>) => void
  track: (event: string, params?: Record<string, unknown>, options?: Record<string, unknown>) => void
  page: () => void
}
type Lintrk = (action: string, params: Record<string, unknown>) => void

declare global {
  interface Window {
    fbq?: (command: string, target: string, params?: Record<string, unknown>, options?: Record<string, unknown>) => void
    gtag?: Gtag
    uetq?: Uet | unknown[]
    ttq?: Ttq
    lintrk?: Lintrk
  }
}

const readCookie = (name: string): string | undefined => {
  if (typeof document === 'undefined') {
    return undefined
  }
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  return match ? decodeURIComponent(match[1]) : undefined
}

/**
 * Copies the ad platforms' first-party cookies onto the PostHog person, so the server-side
 * Conversions APIs can send the real values.
 *
 * **Why the person and not the event.** Three conversions have no browser behind them — the
 * Calendly booking and Mollie's two webhooks — so they carry no cookies of their own. The Manager
 * reads these same cookies from any request that does have them (`App\Support\RequestData`) and
 * writes them to the person, but somebody whose *first* conversion is a booked demo never made
 * such a request. Without this, that visitor reaches the ad platforms with no click id at all.
 *
 * This used to cover Meta only, which left exactly that hole open for TikTok and LinkedIn. Google
 * and Microsoft are not here because their click ids arrive as URL parameters rather than cookies;
 * the Manager reads those from the request and PostHog records them as `$initial_*` besides.
 *
 * Safe to call repeatedly, and only ever sets what is present — an absent cookie must not
 * overwrite a value an earlier visit established.
 */
export function syncAdMatchKeys(): void {
  const cookies: Record<string, string | undefined> = {
    // Meta. `fbc` in particular: PostHog otherwise rebuilds it from `fbclid` with a
    // now() timestamp, which is a different value from the one the click actually created.
    fbc: readCookie('_fbc'),
    fbp: readCookie('_fbp'),
    // TikTok's and LinkedIn's equivalents.
    ttp: readCookie('_ttp'),
    li_fat_id: readCookie('li_fat_id'),
  }

  const present = Object.fromEntries(Object.entries(cookies).filter(([, value]) => Boolean(value)))

  if (Object.keys(present).length > 0) {
    analytics.setPersonProperties(present)
  }
}

/**
 * Fires the browser pixel PageView on client-side route changes, from `PixelPageviews`. The initial
 * pageview of a load is fired by each pixel's own bootstrap snippet, which is what those snippets
 * are for and the only thing that runs reliably before hydration. Bing UET tracks SPA navigation
 * itself and is absent here.
 *
 * No event id: nothing is routed from `$pageview` to a server-side destination, so there is nothing
 * to deduplicate against. See the note in `ad-pixels.tsx` for why attaching PostHog's uuid was
 * removed rather than fixed.
 */
export function firePixelPageview(): void {
  if (typeof window === 'undefined' || !pixelsEnabled) {
    return
  }
  if (meta.pixelId && window.fbq) {
    window.fbq('track', 'PageView')
  }
  if (tiktok.pixelId && window.ttq) {
    window.ttq.page()
  }
  if (google.adsId && window.gtag) {
    window.gtag('event', 'page_view', { send_to: google.adsId })
  }
}

type FireArgs = {
  /** Shared dedup id — the backend-provided eventId. Used as each platform's event/transaction id. */
  eventId?: string
  email?: string
  phone?: string
  firstName?: string
  lastName?: string
  /** Stable cross-system id (the backend client hash) — matches the backend CAPI's external_id. */
  externalId?: string
  value?: number
  currency?: string
}

const clean = (value?: string): string | undefined => {
  const trimmed = value?.trim()
  return trimmed ? trimmed : undefined
}

/**
 * Fires the browser pixel for a conversion on every configured platform with the fullest match
 * data available (email, phone, name, external_id) for advanced/enhanced matching, using the
 * shared `eventId` as the dedup key so each platform counts it once against the server-side CAPI.
 * No-op unless pixels are enabled (production, or the force override).
 */
export function firePixelConversion(type: ConversionType, args: FireArgs): void {
  if (typeof window === 'undefined' || !pixelsEnabled) {
    return
  }
  const { eventId, externalId, value, currency = 'EUR' } = args
  const email = args.email ? normalizeEmail(args.email) : undefined
  const phone = normalizePhone(args.phone)
  const firstName = clean(args.firstName)
  const lastName = clean(args.lastName)
  const amount = value ?? conversionValue[type]
  const names = eventNames[type]

  // Make the real cookies available to the server-side Meta CAPI as soon as we have a conversion.
  syncAdMatchKeys()

  // Meta — advanced matching (the pixel hashes these client-side).
  if (meta.pixelId && window.fbq) {
    const userData = {
      ...(email ? { em: email } : {}),
      ...(phone ? { ph: phone } : {}),
      ...(firstName ? { fn: firstName } : {}),
      ...(lastName ? { ln: lastName } : {}),
      ...(externalId ? { external_id: externalId } : {}),
    }
    if (Object.keys(userData).length > 0) {
      window.fbq('init', meta.pixelId, userData)
    }
    window.fbq('track', names.meta, { value: amount, currency }, eventId ? { eventID: eventId } : undefined)
  }

  // Google Ads — enhanced conversions user data.
  if (google.adsId && google.labels[type] && window.gtag) {
    if (email || phone || firstName || lastName) {
      window.gtag('set', 'user_data', {
        ...(email ? { email } : {}),
        ...(phone ? { phone_number: phone } : {}),
        ...(firstName || lastName
          ? {
              address: {
                ...(firstName ? { first_name: firstName } : {}),
                ...(lastName ? { last_name: lastName } : {}),
              },
            }
          : {}),
      })
    }
    window.gtag('event', 'conversion', {
      send_to: `${google.adsId}/${google.labels[type]}`,
      value: amount,
      currency,
      transaction_id: eventId, // Google dedup key
    })
  }

  // Microsoft / Bing UET — enhanced conversions PII + conversion goal.
  const uetq = window.uetq
  if (bing.uetTagId && uetq && !Array.isArray(uetq) && 'push' in uetq) {
    if (email || phone) {
      uetq.push('set', { pid: { ...(email ? { em: email } : {}), ...(phone ? { ph: phone } : {}) } })
    }
    uetq.push('event', names.bing, { revenue_value: amount, currency, transaction_id: eventId })
  } else if (bing.uetTagId && Array.isArray(uetq)) {
    // UET not initialised yet — queue the event; PII pid is best-effort once loaded.
    ;(uetq as unknown[]).push('event', names.bing, { revenue_value: amount, currency, transaction_id: eventId })
  }

  // TikTok — identify hashes the user data client-side.
  if (tiktok.pixelId && window.ttq) {
    if (email || phone || externalId) {
      window.ttq.identify({
        ...(email ? { email } : {}),
        ...(phone ? { phone_number: phone } : {}),
        ...(externalId ? { external_id: externalId } : {}),
      })
    }
    window.ttq.track(names.tiktok, { value: amount, currency }, eventId ? { event_id: eventId } : undefined)
  }

  // LinkedIn — the Insight Tag matches on its li_fat_id cookie; user data goes via the server CAPI.
  const linkedinConversion = linkedin.conversions[type]
  if (linkedin.partnerId && linkedinConversion && window.lintrk) {
    window.lintrk('track', { conversion_id: Number(linkedinConversion) })
  }
}
